import { useState } from 'react';
import { NewsletterSettings } from '@/hooks/useNewsletterSettings';

interface NewsletterEditorProps {
  settings: NewsletterSettings;
  onSave: (settings: NewsletterSettings) => Promise<boolean>;
}

const bgPositions = [
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Direita' },
  { value: 'left', label: 'Esquerda' },
  { value: 'top', label: 'Topo' },
  { value: 'bottom', label: 'Base' },
  { value: 'right top', label: 'Direita + Topo' },
  { value: 'right bottom', label: 'Direita + Base' },
  { value: 'left top', label: 'Esquerda + Topo' },
  { value: 'left bottom', label: 'Esquerda + Base' },
];

const bgSizes = [
  { value: 'cover', label: 'Cover (cobrir tudo)' },
  { value: 'contain', label: 'Contain (caber todo)' },
  { value: '100% 100%', label: 'Esticar (100%)' },
  { value: '50% auto', label: '50% largura' },
  { value: '75% auto', label: '75% largura' },
  { value: '125% auto', label: '125% largura' },
  { value: '150% auto', label: '150% largura' },
  { value: '200% auto', label: '200% largura' },
  { value: 'auto 100%', label: '100% altura' },
  { value: 'auto 150%', label: '150% altura' },
  { value: 'auto 200%', label: '200% altura' },
];

export default function NewsletterEditor({ settings, onSave }: NewsletterEditorProps) {
  const [form, setForm] = useState<NewsletterSettings>({ ...settings });
  const [saving, setSaving] = useState(false);

  const update = (field: keyof NewsletterSettings, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
          Newsletter
        </h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Preview — renders with actual background image, no color overlay */}
      <div className="bg-background-50 rounded-lg border border-background-200 p-4">
        <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-3">Preview da Seção</p>
        <div
          className="relative rounded-lg overflow-hidden h-48 md:h-56"
          style={{
            backgroundImage: `url(${form.image})`,
            backgroundSize: form.bgSize,
            backgroundPosition: form.bgPosition,
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="relative z-10 flex items-center h-full px-6 md:px-10">
            <div className="max-w-sm">
              <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg leading-tight mb-1">
                {form.heading || 'Sem título'}
              </h3>
              <p className="text-foreground-600 text-xs mb-3">
                {form.subheading || 'Sem subtítulo'}
              </p>
              <span className="inline-block bg-primary-500 text-white text-xs font-bold px-3 py-1.5 rounded-md">
                {form.buttonText || 'Botão'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-6 space-y-5">
        <div>
          <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
            Título
          </label>
          <input
            type="text"
            value={form.heading}
            onChange={e => update('heading', e.target.value)}
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
            Subtítulo
          </label>
          <textarea
            value={form.subheading}
            onChange={e => update('subheading', e.target.value)}
            rows={2}
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50 resize-none"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
            Texto do Botão
          </label>
          <input
            type="text"
            value={form.buttonText}
            onChange={e => update('buttonText', e.target.value)}
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
            Imagem
          </label>
          <input
            type="text"
            value={form.image}
            onChange={e => update('image', e.target.value)}
            placeholder="URL da imagem..."
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
          />
          {form.image && (
            <div className="mt-3 rounded-lg overflow-hidden border border-background-200 h-32 w-48">
              <img src={form.image} alt="Preview" className="w-full h-full object-cover object-top" />
            </div>
          )}
        </div>

        {/* Position controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
              Posição da imagem
            </label>
            <select
              value={form.bgPosition}
              onChange={e => update('bgPosition', e.target.value)}
              className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
            >
              {bgPositions.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">
              Ajuste / Zoom
            </label>
            <select
              value={form.bgSize}
              onChange={e => update('bgSize', e.target.value)}
              className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50"
            >
              {bgSizes.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}